// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { push } from "connected-react-router";
import { withRouter } from "react-router-dom";

import sharedActionCreators from "@modules/shared/actionCreator";
import { getCurrentPath } from "@selectors";
import { route } from "@routes/helper";
import { formatDateTime, transformSecondsInHours } from "@transformers/date";
import { GSGrid, Loader } from "@components";
import * as Paths from "@routes/constants";
import type { EmployeeAbsence } from "@modules/shared/reducer/employee-absences/types";
import { Pagination } from "@components";
import type { PaginationExtendedType } from "@types";
import { isClientAdmin, selectUserType } from "@modules/account/selectors";
import moment from "moment";
import * as employeeAbsenceSelectors from "@modules/shared/selectors/employee-absences";
import { USER_TYPES } from "@modules/account/constants";

type OwnProps = $Exact<{
  clientId: string,
  rowData: Array<EmployeeAbsence>,
  absenceDetailsRoute: $Keys<typeof Paths>,
  employeeId: number,
  getEmployeeAbsences: (page: number) => void,
  pagination: PaginationExtendedType,
  showEditAbsenceModal: (absence: EmployeeAbsence) => void,
  showAbsenceTransferModal: (absence: EmployeeAbsence) => void,
  confirmDeleteAbsence: (absence: EmployeeAbsence) => void,
  canEditAbsence: boolean,
  canDeleteAbsence: boolean,
  canCancelAbsence: boolean,
  canTransferAbsence: boolean,
  showAbsenceCancelReasonsModal: (absence: EmployeeAbsence) => void,
  history: Object,
}>;

type PropsFromState = $Exact<{
  isClientAdmin?: ?boolean,
  currentPath: string,
  isLoading: boolean,
  selectUserType: string,
}>;

type PropsFromDispatch = $Exact<{
  push: typeof push,
  SaveAbsenceRecordAccess: typeof sharedActionCreators.SaveAbsenceRecordAccess,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

function EmployeeAbsenceHistoryTable(props: Props) {
  function handleViewClick(row: Object) {
    const params = {
      clientId: props.clientId,
      employeeId: props.employeeId,
      absenceId: row.id,
    };
    props.history.push(route(props.absenceDetailsRoute, params));
    props.SaveAbsenceRecordAccess(params);
  }

  function columnDefs() {
    const hasRightToEdit = (hasConsentToShare: false | null) => {
      let result = true;

      const userTypes =
        [USER_TYPES.CLIENT_ADMIN, USER_TYPES.MANAGER, USER_TYPES.FULL_HR_ACCESS].indexOf(
          props.selectUserType,
        ) > -1;

      if (!!userTypes && !!hasConsentToShare === false) {
        result = false;
      }

      return result;
    };

    const cols = [
      {
        key: "id",
        name: props.t("ABSENCE_ID"),
        frozen: "left",
      },
      {
        key: "start",
        name: props.t("START_DATE"),
        renderCell: ({ row }: Object) => formatDateTime(row.startsAt) || "",
      },
      {
        key: "last_absence_date",
        name: props.t("LAST_ABSENT_DAY"),
        width: 100,
        renderCell: ({ row }: Object) =>
          row.lastDayOfAbsence ? moment(row.lastDayOfAbsence).format("DD/MM/YYYY") : "",
      },
      {
        key: "rtwAt",
        name: props.t("RTW_DATE"),
        renderCell: ({ row }: Object) => GSGrid.render.dateTime(row.rtwAt),
      },
      {
        key: "workingDaysLost",
        name: props.t("WORKING_DAYS"),
      },
      {
        key: "hours_lost",
        name: props.t("HOURS_LOST"),
        renderCell: ({ row }: Object) =>
          transformSecondsInHours(row.workingTimeLostInSeconds) || "",
      },
      {
        key: "calendarDaysLost",
        name: props.t("CALENDAR_DAYS"),
      },
      {
        key: "absence_type",
        name: props.t("ABSENCE_TYPE"),
        renderCell: ({ row }: *) => GSGrid.render.statusPill(props.t(row.absenceType)),
      },
      {
        key: "absence_reason",
        name: props.t("ABSENCE_REASON"),
        renderCell: ({ row }: Object) =>
          (row.absenceReason || {}).name || props.t("CONSENT_WITHHELD"),
        align: "left",
      },

      {
        key: "status",
        name: props.t("STATUS"),
        renderCell: ({ row }: *) => GSGrid.render.statusPill(props.t(row.status)),
      },
      {
        key: "createdAt",
        name: props.t("DATE_REPORTED"),
        renderCell: ({ row }: Object) => GSGrid.render.dateTime(row.createdAt),
      },
      {
        key: "reportedBy",
        name: props.t("REPORTED_BY"),
        align: "left",
      },
      {
        key: "work_related_header",
        name: props.t("WORK_RELATED_HEADER"),
        renderCell: ({ row }: Object) => (row.isWorkRelated ? props.t("YES") : props.t("NO")),
        align: "left",
      },
      {
        key: "accident_related_header",
        name: props.t("ACCIDENT_RELATED_HEADER"),
        renderCell: ({ row }: Object) => (row.isAccidentRelated ? props.t("YES") : props.t("NO")),
        align: "left",
      },
      {
        key: "fitAt",
        name: props.t("FIT_DATE"),
        renderCell: ({ row }: Object) => GSGrid.render.dateTime(row.fitAt),
      },
      {
        key: props.t("VIEW"),
        frozen: "right",
        width: 60,
        name: props.t("VIEW"),
        onCellClick: (row: Object) => handleViewClick(row),
        renderCell: ({ row }: Object) => GSGrid.render.materialIcon("search"),
      },
    ];

    if (
      props.canEditAbsence ||
      props.canDeleteAbsence ||
      props.canCancelAbsence ||
      props.canTransferAbsence
    ) {
      cols.push({
        name: "ACTION_MENU",
        frozen: "right",
        contextMenuConfig: (row: Object) => {
          const actionMenuConfig = [];

          if (props.canEditAbsence && hasRightToEdit(row.hasConsentToShare) === true) {
            actionMenuConfig.push({
              label: props.t("EDIT"),
              icon: "edit",
              onClick: () => {
                return props.showEditAbsenceModal(row.id);
              },
            });
          }

          if (props.canTransferAbsence) {
            actionMenuConfig.push({
              label: props.t("TRANSFER"),
              icon: "social_distance",
              onClick: () => props.showAbsenceTransferModal(row),
            });
          }

          if (props.canCancelAbsence && row.status !== "CANCELLED") {
            actionMenuConfig.push({
              label: props.t("CANCEL"),
              icon: "cancel",
              onClick: () => props.showAbsenceCancelReasonsModal(row),
            });
          }

          if (props.canDeleteAbsence) {
            actionMenuConfig.push({
              label: props.t("DELETE"),
              icon: "delete_forever",
              onClick: () => props.confirmDeleteAbsence(row),
            });
          }
          return actionMenuConfig;
        },
      });
    }

    return cols;
  }
  const { rowData } = props;

  return (
    <React.Fragment>
      {props.isLoading && <Loader className="position-absolute" inline />}
      <GSGrid columns={columnDefs()} rows={rowData} />
      <Pagination
        className="my-3 d-flex flex-fill justify-content-center"
        currentPage={props.pagination.pageNumber}
        totalPages={props.pagination.totalPages}
        onChange={props.getEmployeeAbsences}
      />
    </React.Fragment>
  );
}

const mapStateToProps = (state): PropsFromState => {
  return {
    isClientAdmin: isClientAdmin(state),
    currentPath: getCurrentPath(state),
    isLoading: employeeAbsenceSelectors.isLoading(state),
    selectUserType: selectUserType(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  push,
  SaveAbsenceRecordAccess: sharedActionCreators.SaveAbsenceRecordAccess,
};

export default compose(
  withRouter,
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EmployeeAbsenceHistoryTable);
